import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { PLAN_LABELS, planBadgeClass, trackDealerPlanEvent } from "../../../constants/automapDealerPlanConstants";
import { fetchPlansDashboard } from "../../../services/automapDealerPlanApi";
import { AdminPlansNav, PlanFeatureCell } from "./AdminPlansShared";

function MetricCard({ label, value, href }) {
  const inner = (
    <>
      <span className="admin-dashboard__metric-label">{label}</span>
      <strong className="admin-dashboard__metric-value">{value ?? "—"}</strong>
    </>
  );
  return href ? (
    <Link to={href} className="admin-dashboard__metric-card admin-dashboard__metric-card--link">{inner}</Link>
  ) : (
    <div className="admin-dashboard__metric-card">{inner}</div>
  );
}

export default function AdminPlansDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchPlansDashboard();
      setData(payload.dashboard);
      trackDealerPlanEvent("admin_plans_dashboard_viewed");
    } catch (err) {
      setError(err.message || "Error al cargar planes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || {};

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Planes"
        title="Planes de concesionario"
        description="Monetización manual, límites y conversión trial-to-paid. Billing por implementar."
      />
      <AdminAutomotiveNav active="plans" />
      <AdminPlansNav active="dashboard" />
      <main className="admin-console__main wrap">
        {loading ? <p className="dealer-state-box">Cargando…</p> : null}
        {error ? <p className="dealer-state-box">{error}</p> : null}
        {!loading && !error && data ? (
          <>
            <div className="admin-dashboard__metrics-grid">
              <MetricCard label="Concesionarios" value={totals.dealers} href="/admin/plans/dealers" />
              <MetricCard label="Pagados activos" value={totals.paidActive} />
              <MetricCard label="Gratis" value={totals.freeDealers} />
              <MetricCard label="En trial" value={totals.trialDealers} href="/admin/dealers/trials" />
              <MetricCard label="Listos convertir" value={totals.readyToConvert} href="/admin/dealers/trials/ready-to-convert" />
              <MetricCard label="Sin asignación explícita" value={totals.pendingAssignment} />
              <MetricCard label="Planes custom" value={totals.customPlans} />
              <MetricCard label="Sobre límite listings" value={totals.overListingLimit} />
              <MetricCard label="Cerca límite leads" value={totals.nearLeadLimit} />
              <MetricCard label="Factura manual pendiente" value={totals.pendingManualInvoice} />
              <MetricCard label="Conversiones este mes" value={totals.conversionsThisMonth} />
            </div>

            <section className="dealer-state-box" style={{ marginTop: "1.5rem" }}>
              <h3>Por plan</h3>
              <ul>
                {Object.entries(totals.byPlan || {}).map(([key, count]) => (
                  <li key={key}>
                    <span className={`admin-badge ${planBadgeClass(key)}`}>{PLAN_LABELS[key] || key}</span> — {count}
                  </li>
                ))}
              </ul>
            </section>

            <section className="dealer-state-box">
              <h3>Forecast ingresos (placeholder)</h3>
              <p className="muted">{data.revenueForecastPlaceholder?.note}</p>
            </section>

            <section className="dealer-state-box">
              <h3>Matriz resumida</h3>
              <div className="import-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Listings</th>
                      <th>Leads/mes</th>
                      <th>Bulk</th>
                      <th>Reportes</th>
                      <th>Destacado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.featureMatrix || []).map((plan) => (
                      <tr key={plan.planKey}>
                        <td>{plan.publicName}</td>
                        <td><PlanFeatureCell value={plan.listingLimit} /></td>
                        <td><PlanFeatureCell value={plan.leadLimitMonthly} /></td>
                        <td><PlanFeatureCell value={plan.bulkUploadAccess} /></td>
                        <td><PlanFeatureCell value={plan.performanceReportsAccess} /></td>
                        <td><PlanFeatureCell value={plan.featuredPlacementEligible} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="import-actions" style={{ marginTop: "0.75rem" }}>
                <Link className="admin-btn admin-btn--primary" to="/admin/plans/dealers">Gestionar planes</Link>
                <Link className="admin-btn" to="/admin/dealers/trials">Ver trials</Link>
                <Link className="admin-btn" to="/admin/plans/features">Ver matriz completa</Link>
              </div>
            </section>
          </>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}
